import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  VirtualizedMultiSelectCombobox,
} from '@roger/ui-components';
import { Download, Loader2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import TablePaginationFooter from '../TablePaginationFooter';
import {
  MAX_AGENCY_FILTER,
  MAX_SEARCH_LENGTH,
  MIN_SEARCH_LENGTH,
  downloadClinicianDirectoryCsv,
  listClinicianDirectoryAgencies,
  searchClinicianDirectory,
  type ClinicianDirectoryAgency,
  type ClinicianDirectoryFilters,
  type ClinicianDirectoryRow,
} from '../../services/clinicianDirectory';
import { getUserFacingApiMessageAsync } from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

// Axios's own fixed error codes: any other code could be caller text.
const AXIOS_CODES = new Set([
  'ERR_BAD_OPTION',
  'ERR_BAD_OPTION_VALUE',
  'ERR_BAD_REQUEST',
  'ERR_BAD_RESPONSE',
  'ERR_CANCELED',
  'ERR_DEPRECATED',
  'ERR_FR_TOO_MANY_REDIRECTS',
  'ERR_INVALID_URL',
  'ERR_NETWORK',
  'ERR_NOT_SUPPORT',
  'ECONNABORTED',
  'ETIMEDOUT',
]);

// Only the status and an axios code: a request error's config holds the search
// text, a name or email, and a logged object reaches Sentry and Session Replay.
const failure = (error: unknown) => {
  const code = (error as { code?: unknown } | null)?.code;
  return {
    status: getHttpStatus(error),
    code: typeof code === 'string' && AXIOS_CODES.has(code) ? code : undefined,
  };
};

/**
 * Contact details of clinician logins across every customer agency, for
 * outreach. Roger and demo logins never appear; inactive ones only on request.
 * The export holds the whole filtered list, not just the page on screen.
 */
export function ClinicianDirectoryPanel() {
  const { showError } = useStore(
    useShallow(state => ({ showError: state.showError }))
  );
  const [agencies, setAgencies] = useState<ClinicianDirectoryAgency[]>([]);
  const [agenciesLoading, setAgenciesLoading] = useState(true);
  const [agenciesError, setAgenciesError] = useState<string | null>(null);
  const [agencyIds, setAgencyIds] = useState<string[]>([]);
  const [text, setText] = useState('');
  const [searchText, setSearchText] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [rows, setRows] = useState<ClinicianDirectoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [exporting, setExporting] = useState(false);

  const latestRequest = useRef(0);
  // The tab unmounts the panel, so a late reply must not toast over another tab.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      latestRequest.current += 1;
    };
  }, []);

  useEffect(() => {
    listClinicianDirectoryAgencies()
      .then(found => {
        if (mounted.current) setAgencies(found);
      })
      .catch(async (error: unknown) => {
        if (!mounted.current) return;
        console.error('Failed to load the directory agencies', failure(error));
        const message = await getUserFacingApiMessageAsync(
          error,
          'Could not load the agency list.'
        );
        if (!mounted.current) return;
        setAgenciesError(message);
        showError(message);
      })
      .finally(() => {
        if (mounted.current) setAgenciesLoading(false);
      });
  }, [showError]);

  // Text under the minimum is no filter, so typing a first letter does not refetch.
  const trimmedText = text.trim();
  const typedSearch =
    trimmedText.length >= MIN_SEARCH_LENGTH ? trimmedText : '';
  const searchPending = typedSearch !== searchText;
  useEffect(() => {
    if (!searchPending) return;
    const timer = setTimeout(() => {
      setSearchText(typedSearch);
      setPageIndex(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [typedSearch, searchPending]);

  const filters: ClinicianDirectoryFilters = useMemo(
    () => ({ agencyIds, text: searchText, includeInactive }),
    [agencyIds, searchText, includeInactive]
  );

  const load = useCallback(
    async (
      current: ClinicianDirectoryFilters,
      offset: number,
      limit: number
    ) => {
      const requestId = ++latestRequest.current;
      setLoading(true);
      try {
        const page = await searchClinicianDirectory(current, offset, limit);
        if (requestId !== latestRequest.current) return;
        setLoadFailed(false);
        setRows(page.items);
        setTotal(page.total);
        // The roster shrank under this page: move to its new last page.
        if (offset > 0 && offset >= page.total) {
          setPageIndex(Math.max(0, Math.ceil(page.total / limit) - 1));
        }
      } catch (error) {
        if (requestId !== latestRequest.current) return;
        setLoadFailed(true);
        setRows([]);
        setTotal(0);
        console.error('Failed to load the clinician directory', failure(error));
        const message = await getUserFacingApiMessageAsync(
          error,
          'Could not load the clinician directory.'
        );
        if (requestId === latestRequest.current) showError(message);
      } finally {
        if (requestId === latestRequest.current) setLoading(false);
      }
    },
    [showError]
  );

  useEffect(() => {
    void load(filters, pageIndex * pageSize, pageSize);
  }, [load, filters, pageIndex, pageSize, reloadToken]);

  const agencyOptions = useMemo(
    () =>
      agencies
        .map(agency => ({
          value: agency.agency_id,
          label: agency.written_name || agency.agency_id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [agencies]
  );

  const exportCsv = async () => {
    setExporting(true);
    try {
      // The loaded filters: Export waits for the search to settle, so this is the table's set.
      await downloadClinicianDirectoryCsv(filters);
    } catch (error) {
      console.error('Failed to export the clinician directory', failure(error));
      const message = await getUserFacingApiMessageAsync(
        error,
        'Could not export the clinician directory. Roger Docs may be unavailable; try again.'
      );
      if (mounted.current) showError(message);
    } finally {
      if (mounted.current) setExporting(false);
    }
  };

  const pageCount = Math.ceil(total / pageSize);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Clinician Directory</CardTitle>
        <CardDescription>
          Clinician contacts across every customer agency. Export downloads
          every matching clinician, not only this page.
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-4'>
        <div className='flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center'>
          <VirtualizedMultiSelectCombobox
            ariaLabel='Agencies'
            options={agencyOptions}
            value={agencyIds}
            onValueChange={ids => {
              setAgencyIds(ids.slice(0, MAX_AGENCY_FILTER));
              setPageIndex(0);
            }}
            placeholder='All agencies'
            searchPlaceholder='Search agencies...'
            isLoading={agenciesLoading}
            error={agenciesError ?? undefined}
            className='w-full md:w-72'
          />
          <Input
            aria-label='Search clinicians'
            maxLength={MAX_SEARCH_LENGTH}
            placeholder='Name, username or email'
            value={text}
            onChange={event => setText(event.target.value)}
            className='w-full md:w-72'
          />
          <div className='flex items-center gap-2'>
            <Checkbox
              id='clinician-directory-include-inactive'
              checked={includeInactive}
              onCheckedChange={checked => {
                setIncludeInactive(checked === true);
                setPageIndex(0);
              }}
            />
            <Label htmlFor='clinician-directory-include-inactive'>
              Include inactive
            </Label>
          </div>
          <Button
            variant='outline'
            size='sm'
            className='md:ml-auto'
            disabled={exporting || loading || searchPending || total === 0}
            onClick={() => void exportCsv()}
          >
            {exporting ? (
              <Loader2 className='h-4 w-4 animate-spin' aria-hidden='true' />
            ) : (
              <Download className='h-4 w-4' aria-hidden='true' />
            )}
            Export CSV
          </Button>
        </div>
        {agencyIds.length >= MAX_AGENCY_FILTER && (
          <p className='text-xs text-muted-foreground'>
            Up to {MAX_AGENCY_FILTER} agencies can be picked. Clear the filter
            to search every agency.
          </p>
        )}
        {loading ? (
          <Loader2 className='h-4 w-4 animate-spin' aria-label='Loading' />
        ) : loadFailed ? (
          <div className='flex flex-wrap items-center gap-3 text-sm text-destructive'>
            <span>Could not load the clinician directory.</span>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setReloadToken(n => n + 1)}
            >
              Try again
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <p className='text-sm text-muted-foreground'>No clinicians found.</p>
        ) : (
          <div className='overflow-auto'>
            <Table className='min-w-[960px]'>
              <TableHeader>
                <TableRow>
                  <TableHead>Agency</TableHead>
                  <TableHead>Agency ID</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>First name</TableHead>
                  <TableHead>Last name</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Active</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={`${row.agency_id}/${row.username}/${index}`}>
                    <TableCell>{row.agency_name}</TableCell>
                    <TableCell>{row.agency_id}</TableCell>
                    <TableCell>{row.username}</TableCell>
                    <TableCell>{row.first_name}</TableCell>
                    <TableCell>{row.last_name}</TableCell>
                    <TableCell>{row.title}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell className='whitespace-nowrap'>
                      {row.phone_number}
                    </TableCell>
                    <TableCell>{row.active ? 'Yes' : 'No'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {!loadFailed && (
          <TablePaginationFooter
            pageIndex={pageIndex}
            pageCount={pageCount}
            totalCount={total}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            canPreviousPage={pageIndex > 0}
            canNextPage={pageIndex + 1 < pageCount}
            onPageSizeChange={size => {
              setPageSize(size);
              setPageIndex(0);
            }}
            onPreviousPage={() => setPageIndex(index => Math.max(0, index - 1))}
            onNextPage={() => setPageIndex(index => index + 1)}
          />
        )}
      </CardContent>
    </Card>
  );
}
